import { useQueryClient } from '@tanstack/react-query';
import type { WhatsAppInboxChat, WhatsAppInboxMessage } from '@zrsystem/contracts';
import { useEffect } from 'react';

import {
  createWhatsAppInboxSocket,
  readWhatsAppInboxRealtimeMessage,
  readWhatsAppInboxRealtimePlaceId,
  WHATSAPP_INBOX_EVENTS,
  type WhatsAppInboxSocketErrorPayload,
} from '@/features/notification/api/whatsapp-inbox-socket.js';
import {
  sortWhatsAppInboxChats,
  type WhatsAppInboxChatsResult,
} from '@/features/notification/hooks/use-whatsapp-inbox-chats.js';
import { mergeWhatsAppInboxMessages } from '@/features/notification/hooks/use-whatsapp-inbox-messages.js';
import { resolveWhatsAppInboxChatId } from '@/features/notification/lib/resolve-whatsapp-inbox-chat-id.js';
import { whatsAppInboxMessagePreviewLabel } from '@/features/notification/lib/whatsapp-inbox-labels.js';
import { readWhatsAppInboxRealtimeSession } from '@/features/notification/lib/whatsapp-inbox-realtime-session.js';
import {
  markWhatsAppInboxChatSeen,
  recordWhatsAppInboxUnreadBump,
} from '@/features/notification/lib/whatsapp-inbox-unread.js';
import { notificationQueryKeys } from '@/features/notification/query-keys.js';
import { getAccessToken } from '@/shared/auth/auth-storage.js';

export interface UseWhatsAppInboxSocketOptions {
  readonly enabled: boolean;
  readonly placeId: string;
}

/**
 * Inbound messages bump unread only when that chat is not the one open on screen.
 * Opening a chat does not clear unread — the next WAHA listing is the source of truth.
 */
export function nextWhatsAppInboxUnreadCount(
  current: number,
  chatId: string,
  message: WhatsAppInboxMessage,
  selectedChatId: string | null,
): number {
  if (message.fromMe || selectedChatId === chatId) {
    return current;
  }
  return current + 1;
}

export function patchChatsWithMessage(
  chats: readonly WhatsAppInboxChat[],
  chatId: string,
  message: WhatsAppInboxMessage,
  selectedChatId: string | null,
): WhatsAppInboxChat[] {
  const previewBody = whatsAppInboxMessagePreviewLabel(message);
  const known = chats.some((chat) => chat.id === chatId);

  const nextChats = known
    ? chats.map((chat) => {
        if (chat.id !== chatId) {
          return chat;
        }
        return {
          ...chat,
          unreadCount: nextWhatsAppInboxUnreadCount(
            chat.unreadCount,
            chatId,
            message,
            selectedChatId,
          ),
          lastMessage: {
            body: previewBody,
            timestamp: message.timestamp,
          },
        };
      })
    : [
        ...chats,
        {
          id: chatId,
          picture: null,
          name: null,
          unreadCount: nextWhatsAppInboxUnreadCount(0, chatId, message, selectedChatId),
          lastMessage: {
            body: previewBody,
            timestamp: message.timestamp,
          },
        },
      ];

  return sortWhatsAppInboxChats(nextChats);
}

export function patchWhatsAppInboxChatsResult(
  current: WhatsAppInboxChatsResult | undefined,
  chatId: string,
  message: WhatsAppInboxMessage,
  selectedChatId: string | null,
): WhatsAppInboxChatsResult {
  return {
    chats: patchChatsWithMessage(current?.chats ?? [], chatId, message, selectedChatId),
    accountUnavailable: current?.accountUnavailable ?? false,
  };
}

export function useWhatsAppInboxSocket({ enabled, placeId }: UseWhatsAppInboxSocketOptions): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || placeId.length === 0) {
      return;
    }

    const token = getAccessToken();
    if (token === null) {
      readWhatsAppInboxRealtimeSession().onError?.('Sessão necessária para o WhatsApp');
      return;
    }

    const socket = createWhatsAppInboxSocket(token);

    const joinPlace = (): void => {
      socket.emit(WHATSAPP_INBOX_EVENTS.join, { placeId });
    };

    socket.on('connect', joinPlace);

    socket.on(WHATSAPP_INBOX_EVENTS.message, (payload: unknown) => {
      const eventPlaceId = readWhatsAppInboxRealtimePlaceId(payload);
      if (eventPlaceId !== null && eventPlaceId !== placeId) {
        return;
      }

      const message = readWhatsAppInboxRealtimeMessage(payload);
      if (message === null) {
        return;
      }
      const chatId = resolveWhatsAppInboxChatId(message);
      const openChatId = readWhatsAppInboxRealtimeSession().selectedChatId;

      const next = patchWhatsAppInboxChatsResult(
        queryClient.getQueryData<WhatsAppInboxChatsResult>(
          notificationQueryKeys.whatsappInboxChats(placeId),
        ),
        chatId,
        message,
        openChatId,
      );
      queryClient.setQueryData(notificationQueryKeys.whatsappInboxChats(placeId), next);

      const updated = next.chats.find((chat) => chat.id === chatId);
      if (openChatId === chatId) {
        markWhatsAppInboxChatSeen(placeId, chatId, message.timestamp);
        queryClient.setQueryData(
          notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
          (current: WhatsAppInboxMessage[] | undefined) =>
            mergeWhatsAppInboxMessages(current ?? [], [message]),
        );
      } else if (updated !== undefined && !message.fromMe) {
        recordWhatsAppInboxUnreadBump(placeId, chatId, updated.unreadCount);
      }
    });

    socket.on(WHATSAPP_INBOX_EVENTS.error, (payload: WhatsAppInboxSocketErrorPayload) => {
      readWhatsAppInboxRealtimeSession().onError?.(payload.message ?? 'Falha no WhatsApp');
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled, placeId, queryClient]);
}
